import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'

export { default as Badge } from './Badge.vue'

/*
 * Reconciled to the canonical components/data/Badge.jsx, which differs from the
 * shadcn default on every axis: rounded-ds-sm rather than a pill, bold rather
 * than semibold, and tone × fill × size rather than one `variant` list. Keeping
 * shadcn's shape here would have meant re-theming every badge later.
 *
 * `tone` is the hue and `variant` is the fill, so the twelve pairings come from
 * compoundVariants rather than twelve hand-written variant names.
 */
export const badgeVariants = cva(
  [
    'inline-flex items-center gap-1.5 whitespace-nowrap',
    'font-ds font-bold leading-snug tracking-ds-snug',
    'rounded-ds-sm',
  ].join(' '),
  {
    variants: {
      tone: {
        brand: '',
        neutral: '',
        success: '',
        warning: '',
        danger: '',
        info: '',
      },
      variant: {
        soft: '',
        solid: '',
      },
      size: {
        // Canonical 2px/8px at --fs-2xs (11px), which Tailwind's scale has no
        // step for; and 3px/10px at --fs-xs, rounded to the 4px grid.
        sm: 'px-2 py-0.5 text-[0.6875rem]',
        md: 'px-2.5 py-1 text-xs',
      },
    },
    compoundVariants: [
      // Soft fills use the a11y-corrected `-subtle` pairs — the canonical
      // pairing of a 600 step on a 50 wash fails AA for green and amber.
      { tone: 'brand', variant: 'soft', class: 'bg-accent text-accent-foreground' },
      { tone: 'neutral', variant: 'soft', class: 'bg-secondary text-secondary-foreground' },
      { tone: 'success', variant: 'soft', class: 'bg-success-subtle text-success-subtle-foreground' },
      { tone: 'warning', variant: 'soft', class: 'bg-warning-subtle text-warning-subtle-foreground' },
      { tone: 'danger', variant: 'soft', class: 'bg-destructive-subtle text-destructive-subtle-foreground' },
      { tone: 'info', variant: 'soft', class: 'bg-info-subtle text-info-subtle-foreground' },

      { tone: 'brand', variant: 'solid', class: 'bg-primary text-primary-foreground' },
      // (derived) Canonical solid neutral is neutral-700 on white; @ds has no
      // token at that step, so this inverts against the page instead.
      { tone: 'neutral', variant: 'solid', class: 'bg-foreground text-background' },
      { tone: 'success', variant: 'solid', class: 'bg-success text-success-foreground' },
      { tone: 'warning', variant: 'solid', class: 'bg-warning text-warning-foreground' },
      { tone: 'danger', variant: 'solid', class: 'bg-destructive text-destructive-foreground' },
      { tone: 'info', variant: 'solid', class: 'bg-info text-info-foreground' },
    ],
    defaultVariants: {
      tone: 'brand',
      variant: 'soft',
      size: 'md',
    },
  },
)

export type BadgeVariants = VariantProps<typeof badgeVariants>
